import React, { useEffect, useState } from 'react';
import { Alert, Button, CircularProgress, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { PasswordOutlined } from '@mui/icons-material';
import type { OtpChallenge } from '@services/api/client';

interface Props {
  challenge: OtpChallenge;
  /** Checks the code; throws with a message when it's wrong. */
  onVerify: (code: string) => Promise<void>;
  onBack: () => void;
  submitLabel?: string;
}

/** Second sign-in step: the 6-digit code emailed to the approver. */
export const OtpCodeStep: React.FC<Props> = ({ challenge, onVerify, onBack, submitLabel = 'Verify and sign in' }) => {
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [left, setLeft] = useState(challenge.expires_in);

  useEffect(() => {
    const id = window.setInterval(() => setLeft((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearInterval(id);
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) {
      setErr('Enter the 6-digit code.');
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      await onVerify(code);
    } catch (e: any) {
      setErr(e?.error?.message || e?.message || 'Verification failed');
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  const mm = Math.floor(left / 60);
  const ss = String(left % 60).padStart(2, '0');

  return (
    <form onSubmit={submit} noValidate>
      <Stack spacing={2}>
        <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary' }}>
          A verification code was sent to <b>{challenge.sent_to}</b>. Ask for the code and enter it below.
        </Typography>
        {err && (
          <Alert severity="error" onClose={() => setErr(null)}>
            {err}
          </Alert>
        )}
        <TextField
          label="Verification code"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          autoFocus
          fullWidth
          autoComplete="one-time-code"
          disabled={loading || left === 0}
          inputProps={{ inputMode: 'numeric', maxLength: 6, style: { letterSpacing: 8, fontSize: '1.3rem' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PasswordOutlined sx={{ color: 'text.secondary', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          helperText={left > 0 ? `Code expires in ${mm}:${ss}` : 'Code expired — go back and sign in again.'}
        />
        <Button
          fullWidth
          type="submit"
          size="large"
          variant="contained"
          disabled={loading || left === 0}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : undefined}
          sx={{ py: 1.4, fontWeight: 700 }}
        >
          {loading ? 'Verifying…' : submitLabel}
        </Button>
        <Button fullWidth onClick={onBack} disabled={loading}>
          Back to sign in
        </Button>
      </Stack>
    </form>
  );
};

export default OtpCodeStep;
